export class AudioSystem {
  private context: AudioContext | null = null;
  private unlocked = false;
  private ambience?: { source: OscillatorNode; gain: GainNode };

  constructor() {
    const unlock = () => {
      void this.unlock();
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
  }

  async unlock(): Promise<void> {
    if (this.unlocked) return;
    const AudioContextClass =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextClass) return;
    this.context = new AudioContextClass();
    await this.context.resume();
    this.unlocked = true;
  }

  cue(kind: 'cut' | 'select' | 'capture' | 'record-start' | 'record-stop'): void {
    if (!this.context || this.context.state !== 'running') return;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    const now = this.context.currentTime;
    const settings = {
      cut: ['sine', 220, 390, 0.12, 0.045],
      select: ['triangle', 410, 560, 0.09, 0.035],
      capture: ['square', 920, 260, 0.1, 0.04],
      'record-start': ['sine', 330, 660, 0.22, 0.05],
      'record-stop': ['sine', 660, 330, 0.22, 0.05],
    } as const;
    const [type, start, end, duration, volume] = settings[kind];
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(start, now);
    oscillator.frequency.exponentialRampToValueAtTime(end, now + duration);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(volume, now + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    oscillator.connect(gain).connect(this.context.destination);
    oscillator.start(now);
    oscillator.stop(now + duration + 0.02);
  }

  setAmbience(active: boolean): void {
    if (!this.context || this.context.state !== 'running') return;
    if (!active) {
      if (this.ambience) {
        this.ambience.gain.gain.exponentialRampToValueAtTime(0.0001, this.context.currentTime + 0.35);
        this.ambience.source.stop(this.context.currentTime + 0.4);
        this.ambience = undefined;
      }
      return;
    }
    if (this.ambience) return;
    const source = this.context.createOscillator();
    const gain = this.context.createGain();
    source.type = 'sine';
    source.frequency.value = 71;
    gain.gain.value = 0.0001;
    gain.gain.exponentialRampToValueAtTime(0.012, this.context.currentTime + 1.2);
    source.connect(gain).connect(this.context.destination);
    source.start();
    this.ambience = { source, gain };
  }

  dispose(): void {
    this.setAmbience(false);
    void this.context?.close();
    this.context = null;
  }
}
